import React, { useEffect, useState } from "react";
import { FaEdit, FaTrash } from "react-icons/fa";
import { FiDownload } from "react-icons/fi";

const ItemsTable = ({ items, onEdit, onDelete }) => {
  const [deleteIndex, setDeleteIndex] = useState(null); // Track the index of the item to delete
  const [isConfirmOpen, setIsConfirmOpen] = useState(false); // Manage confirmation dialog visibility

  console.log(items);

  // Function to handle CSV sample download
  const handleDownloadSampleCSV = () => {
    // CSV header in the required format
    const headers = [
      "item_name",
      "batchNumber",
      "hsn",
      "inwardQuantity",
      "pricePerUnit",
      "mrp",
      "unitOfMeasure",
      "cgstRate",
      "sgstRate",
      "igstRate",
      "receivedDate",
      "expiryDate",
    ];
    const csvContent = headers.join(",") + "\n"; // Only header row as sample

    // Create a Blob with CSV MIME type
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    // Create a temporary link to trigger download
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_batch_upload.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Functions for delete confirmation
  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setIsConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteIndex !== null) {
      onDelete(deleteIndex);
    }
    setIsConfirmOpen(false);
    setDeleteIndex(null);
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setDeleteIndex(null);
  };

  return (
    <div className="mb-8">
      {/* Header section with title and sample CSV download button */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-2xl font-semibold text-gray-800">Item Details</h3>
        <button
          onClick={handleDownloadSampleCSV}
          className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
          title="Download Sample CSV"
        >
          <FiDownload className="text-lg" />
          Sample CSV
        </button>
      </div>

      {/* Instructional text */}
      <p className="text-sm text-gray-600 mb-4">
        To upload multiple items via CSV, please download the sample file above
        and populate it with your data using the specified format.
        Alternatively, you can add individual items or batches manually using
        the "Add Item" button.
      </p>

      <div className="overflow-x-auto rounded-lg shadow-md">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-gray-100 text-gray-700">
            <tr className="text-sm font-medium uppercase">
              {[
                "Item Name",
                "HSN Code",
                "Batch No.",
                "Location",
                "Received Date",
                "Expiry Date",
                "Pack Qty",
                "Purchase UoM",
                "Sales UoM",
                "Effective Qty",
                "Batch Rate (₹)",
                "Eff. Batch Rate (₹/Sale UoM)",
                "MRP (₹)",
                "Eff. MRP (₹/Sale UoM)",
                "Taxable Value (₹)",
                "CGST (₹)",
                "SGST (₹)",
                "IGST (₹)",
                "Total GST (₹)",
                "Total Amt (₹)",
                "Profit/Unit (₹)",
                "Profit Margin (%)",
                "Actions",
              ].map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-left whitespace-nowrap border-b"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items?.length > 0 ? (
              items.map((item, index) => (
                <tr
                  key={index}
                  className="hover:bg-gray-50 transition-colors text-gray-700 text-sm"
                >
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.item?.item_name || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.hsn || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.batchNumber || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.location?.name || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.receivedDate || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.expiryDate || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.inwardQuantity || 0}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.uop?.unit_name || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.unitOfMeasure || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {item?.effectiveQuantity || "-"}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.pricePerUnit || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.effectivePricePerBase || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.price || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.effectiveMRPPerBase || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.taxableValue || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.cgst || 0).toFixed(2)} (
                    {parseFloat(item?.cgstRate || 0).toFixed(2)}%)
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.sgst || 0).toFixed(2)} (
                    {parseFloat(item?.sgstRate || 0).toFixed(2)}%)
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.igst || 0).toFixed(2)} (
                    {parseFloat(item?.igstRate || 0).toFixed(2)}%)
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.totalGST || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.totalAmount || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    ₹{parseFloat(item?.profitPerUnit || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    {parseFloat(item?.profitMargin || 0).toFixed(2)}%
                  </td>
                  <td className="px-4 py-3 border-b whitespace-nowrap">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onEdit(index)}
                        className="text-blue-500 hover:text-blue-700"
                        title="Edit Item"
                      >
                        <FaEdit />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Item"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={23}
                  className="px-4 py-3 border-b text-sm text-gray-500 text-center"
                >
                  No items added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {isConfirmOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 shadow-lg">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Confirm Deletion
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this item? This action cannot be
              undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsTable;
